import {FormCopyInput, IForm, IFormHeaderAll} from './form';
import {IFormDefinition, IFormField, IFormVisit} from '../builder/formDefinition';
import {IdName, IdSName} from '../../shared/common-interfaces';
import {Injectable} from '@angular/core';
import {MIHttpClientService} from '../../shared/mi-http-client.service';
import {NotificationService} from '../../shared/notification.service';
import {ProjectsService} from '../../batches/projects/projects.service';
import {StoresService} from '../../stores/services/stores.service';
import {BehaviorSubject, forkJoin, Observable, of, Subject} from 'rxjs';
import {isNil} from 'lodash-es';
import {map, mergeMap, switchMap, take} from 'rxjs/operators';
import {sortByField} from '../../shared/custom-rxjs-operators';
import {PhotoCategoryService} from "../photo-categories/photo-category.service";
import {PermissionService} from "../../core/permission.service";


@Injectable({
  providedIn: 'root',
})
export class FormsService {
  formChanged: Subject<void> = new Subject<void>();

  public informationalTextSubject = new BehaviorSubject<IFormField>(null);
  informationalTextSubject$ = this.informationalTextSubject.asObservable();

  constructor(
    private miClient: MIHttpClientService,
    private notificationService: NotificationService,
    private projectsService: ProjectsService,
    private storesService: StoresService,
    private photoCategoryService: PhotoCategoryService,
    private permissionService: PermissionService,
  ) {
  }


  getNewVisitWithStoreByFormId(
    formId: number,
    storeId?: number,
  ): Observable<any[]> {
    const newVisit$ = this.getNewVisitByFormId(formId, storeId);
    let store$;
    if (storeId !== null && storeId !== undefined) {
      store$ = this.storesService.getStore(storeId);
    } else {
      store$ = of(null);
    }
    const allCategories$ = this.photoCategoryService.getPhotoCategories();
    return forkJoin([newVisit$, store$, allCategories$]);
  }

  getNewVisitByFormId(
    formId: number,
    storeId?: number,
  ): Observable<IFormVisit> {
    let storeQueryParam = '';
    if (storeId !== null && storeId !== undefined) {
      storeQueryParam = '?storeId=' + storeId;
    }
    return this.miClient.get<IFormVisit>(`forms/v2/${formId}/new-visit${storeQueryParam}`);
  }

  getAllFormsWithUsableInfo(): Observable<IFormHeaderAll[]> {
    const allFormsObs = this.getAllForms();
    const myFormsObs = this.getMyForms();
    return forkJoin([allFormsObs, myFormsObs]).pipe(
      map(results => {

        let allForms = results[0];
        const usableForms = results[1];
        allForms = allForms.map(form => {
          form.usableByCurrentUser = usableForms.filter(myForm => myForm.ID === form.id).length > 0;
          return form;
        });
        return allForms;
      }),
    );
  }

  getAllForms(): Observable<IFormHeaderAll[]> {
    return this.miClient.get<IFormHeaderAll[]>('forms/allForms').pipe(sortByField('formName'));
  }

  getAllActiveForms(storeForms?: boolean): Observable<IdSName[]> {
    return this.getAllForms().pipe(
      map(results => results.filter(item => item.active && (!isNil(storeForms) ? item.storeStamp === storeForms : true))),
      map(results => results.map(item => {
        return {id: `${item.id}`, name: item.formName};
      })),
      sortByField('name'),
    );
  }

  getFormsBasedOnPermissions(storeForms?: boolean) : Observable<IdSName[]> {
    return this.permissionService.hasAtLeastOnePermission('DYNAMIC_FORM_CREATE', 'DYNAMIC_FORM_MODIFY')
      .pipe(take(1), mergeMap((isFormAdmin) => {
        if (isFormAdmin) {
          return this.getAllActiveForms(storeForms);
        } else {
          return this.getMyFormsFiltered(storeForms);
        }
      }));
  }


  getFormQuestions(id: number): Observable<IdSName[]> {
    return this.getIdNameList('forms/questionsForForm/', id);
  }

  getFormQuestionsForPhotos(id: number): Observable<IdSName[]> {
    return this.getIdNameList('forms/photoQuestions/', id);
  }

  getQuestionAnswers(id: number): Observable<IdSName[]> {
    return this.getIdNameList('forms/choicesForChoiceQuestion/', id);
  }

  getMyForms(): Observable<IForm[]> {
    return this.miClient.get<any>('forms/v5?pageSize=500').pipe(
      map((projects) => projects['list']),
    );
  }

  getMyFormsFiltered(storeForms?: boolean): Observable<IdSName[]> {
    return this.miClient.get<IForm[]>('forms/v5?pageSize=500').pipe(
      map((result) => result['list']),
      map(forms => forms.filter(item => (!isNil(storeForms) ? item.isStoreStamp === storeForms : true))),
      sortByField('FormName'),
      map(results => results.map(form => {
          return {
            id: `${form.ID}`,
            name: form.FormName,
          };
        },
      )),
    );
  }

  getStoreFormsFiltered(storeId: number): Observable<IdName[]> {
    return this.miClient.get<IForm[]>(`forms/v5?storeId=${storeId}&onlyStore=true`).pipe(
      map((projects) => projects['list']),
      sortByField('FormName'),
      map(results => results.map(form => {
          return {
            id: form.ID,
            name: form.FormName,
          };
        },
      )),
    );
  }


  getFormDefinition(formId: number): Observable<IFormDefinition> {
    return this.miClient.get<IFormDefinition>('forms/' + formId);
  }

  create(definition: IFormDefinition): Observable<string> {
    return this.miClient.postNoJSON('forms', definition, {responseType: 'text'})
      .pipe(
        mergeMap(
          id => {
            // if projects were assigned make an additional call
            if (id && definition.assignedProjects && definition.assignedProjects.length > 0) {
              return this.projectsService.assignFormToProjects(+id, definition.assignedProjects);
            } else {
              return of(id);
            }
          },
        ),
      );
  }

  update(definition: IFormDefinition): Observable<string> {
    return this.miClient.postNoJSON('forms/' + definition.formId, definition, {responseType: 'text'})
      .pipe(
        mergeMap(result => {
            // if projects were assigned make an additional call
            if (definition.assignedProjects) {
              return this.projectsService.assignFormToProjects(+definition.formId, definition.assignedProjects);
            } else {
              return of(result);
            }
          },
        ),
      );
  }

  delete(id: number): Observable<void> {
    return this.miClient.delete<void>('forms/' + id);
  }

  copy(id: number, input: FormCopyInput): Observable<string> {
    return this.miClient.postNoJSON(`forms/${id}/clone`, input, { responseType: 'text' });
  }

  uploadStoreCsv(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('photo', file, file.name);
    return this.miClient.postNoJSON('storeVisibilityCSV',
      formData,
      {responseType: 'text'},
    );
  }

  uploadInformationalPhoto(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('photo', file, file.name);
    return this.miClient.postNoJSON('informationalPhoto',
      formData,
      {responseType: 'text'},
    );
  }

  private getIdNameList(relativeUrl: string, id: number): Observable<IdSName[]> {
    if (isNil(id) || isNaN(id) || id === 0) {
      return of([]);
    }

    return this.miClient.get(relativeUrl + id).pipe(
      map(items => {
        let mappedItems = (<any[]>items).map(el => {
          return { id: el[0], name: el[1] }
        });
        return mappedItems;
      })
    );
  }

  checkIfFormNameUnique(formName: string) {
    return this.miClient.get(`forms/checkFormNameExists?formName=${formName}`);
  }
  customAttributeViewPermissions: {[attributeType: string]: string} = {
    STORE: 'STORE_ATTRIBUTES_VIEW',
    CONTACT: 'CONTACT_ATTRIBUTES_USE',
    USER: 'USER_ATTRIBUTES_USE'
  }
  customAttributeUsePermissions: {[attributeType: string]: string} = {
    STORE: 'STORE_ATTRIBUTES_USE',
    CONTACT: 'CONTACT_ATTRIBUTES_USE',
    USER: 'USER_ATTRIBUTES_USE'
  }
  customAttributeManagePermissions: {[attributeType: string]: string} = {
    STORE: 'STORE_ATTRIBUTES_MANAGE',
    CONTACT: 'CONTACT_ATTRIBUTES_MANAGE',
    USER: 'USER_ATTRIBUTES_MANAGE'
  }

  getCustomAttributesForm(customAttributesType: string): Observable<IFormDefinition> {
    const viewPermissionKey = this.customAttributeViewPermissions[customAttributesType];
    const usePermissionKey = this.customAttributeUsePermissions[customAttributesType];
    const managePermissionKey = this.customAttributeManagePermissions[customAttributesType];

    return this.permissionService.hasAtLeastOnePermission(viewPermissionKey, usePermissionKey, managePermissionKey).pipe(take(1),switchMap(permission => {
      if (!permission) {
        return of(null);
      }
      return this.miClient.get<IFormDefinition>('forms/customAttributes/' + customAttributesType);
    }));
  }

  updateCustomAttributesForm(definition: IFormDefinition, customAttributesType: string): Observable<string> {
    return this.miClient.postNoJSON('forms/customAttributes/' + customAttributesType + '/' + definition.formId, definition, {responseType: 'text'});
  }


}
